/**
 * The Level Up builder's wire contract (`/api/v1/level-up/manage`,
 * docs/LEVEL_UP.md Phase 2): request schemas, response shapes and the coded
 * errors of the five builder RPCs.
 *
 * Every version is created through `POST /import` (new assessment, imported
 * file, "new draft from vN") and saved whole through `PUT /versions/:id`; the
 * SQL writer diffs the package by key.
 */
import { z } from "zod";
import { DUAL_SCOPE_TYPE_ERROR_CODES } from "./dualScopeTypes.js";
import { levelUpPackageSchema } from "../levelUp/package.js";
// ---------------------------------------------------------------------------
// Requests
// ---------------------------------------------------------------------------
/** `POST /import` — no `assessment_id` creates the assessment; with one, a new draft of it. */
export const importLevelUpPackageSchema = z
    .object({
    package: levelUpPackageSchema,
    assessment_id: z.string().uuid().optional(),
})
    .strict();
/**
 * `PUT /versions/:versionId` — the whole package plus the optimistic-lock
 * token. `expected_updated_at` is the version's `updated_at` exactly as
 * PostgREST returned it (microseconds): never round-trip it through `Date`,
 * which keeps milliseconds only and would make every save `STALE`.
 */
export const saveLevelUpVersionSchema = z
    .object({
    package: levelUpPackageSchema,
    expected_updated_at: z.string().trim().min(1).max(64),
})
    .strict();
// ---------------------------------------------------------------------------
// Responses
// ---------------------------------------------------------------------------
export const LEVEL_UP_VERSION_STATUSES = ["draft", "published", "retired"];
// ---------------------------------------------------------------------------
// Coded errors (the web reads `err.envelope.code`; i18n `errors.<code>`)
// ---------------------------------------------------------------------------
export const LEVEL_UP_ERROR_CODES = {
    /** 409 — RV014: import of a new assessment whose key the org already uses. */
    keyExists: "KEY_EXISTS",
    /** 409 — RV015: import into an assessment that already has a draft. */
    draftExists: "DRAFT_EXISTS",
    /** 409 — RV016: the version changed since it was loaded (`expected_updated_at`). */
    stale: "STALE",
    /** 400 — RV017: the draft fails a publish check; the message is `PUBLISH_INVALID|<json problems>`. */
    publishInvalid: "PUBLISH_INVALID",
    /** 409 — RV018: publish / delete-draft on a version that is not a draft. */
    notDraft: "NOT_DRAFT",
    /** 409 — RV019: delete of an assessment that holds more than drafts. */
    deleteRefused: "DELETE_REFUSED",
    /** 403 — RV020: an admin-only action (the house `admin_required` envelope). */
    adminRequired: DUAL_SCOPE_TYPE_ERROR_CODES.adminRequired,
    /** 409 — a structure change on a published version (the Phase 1 guard's 23514, mapped by the API). */
    publishedStructure: "PUBLISHED_STRUCTURE",
    /** 409 — a unique violation (23505), mapped by the API. */
    conflict: "CONFLICT",
};
/** The builder RPCs' custom SQLSTATEs → HTTP status + coded envelope. Map by SQLSTATE, never by message. */
export const LEVEL_UP_RPC_SQLSTATES = {
    RV014: { status: 409, code: LEVEL_UP_ERROR_CODES.keyExists },
    RV015: { status: 409, code: LEVEL_UP_ERROR_CODES.draftExists },
    RV016: { status: 409, code: LEVEL_UP_ERROR_CODES.stale },
    RV017: { status: 400, code: LEVEL_UP_ERROR_CODES.publishInvalid },
    RV018: { status: 409, code: LEVEL_UP_ERROR_CODES.notDraft },
    RV019: { status: 409, code: LEVEL_UP_ERROR_CODES.deleteRefused },
    RV020: { status: 403, code: LEVEL_UP_ERROR_CODES.adminRequired },
};
//# sourceMappingURL=levelUpManage.js.map